import { useCompleteFragment } from '@eluve/apollo-client';
import { ResultOf, graphql } from '@eluve/graphql.tada';

import { useAppointmentId } from './appointment.context';
import { appointmentLlmOutputFragment } from './insights.operations';
import { checkSummarizationStatus } from './utils/checkSummarizationStatus';

export const appointmentLlmJobFragment = graphql(`
  fragment AppointmentLlmJob on AppointmentLlmJobs @_unmask {
    __typename
    jobId
    llmOutputType
    jobStatus
    numAttempts
    createdAt
  }
`);

export type AppointmentLlmJob = ResultOf<typeof appointmentLlmJobFragment>;

export const appointmentActivityFragment = graphql(
  `
    fragment appointmentActiveState on Appointments @_unmask {
      __typename
      id
      updatedAt
      isSummarizationInProgress
      isAnySummarizationInProgress
      isTranscriptionInProgress
      isPendingAudioFileUpload

      appointment_llm_jobs(
        distinctOn: llmOutputType
        orderBy: [{ llmOutputType: ASC }, { createdAt: DESC }]
      ) {
        ...AppointmentLlmJob
      }

      appointment_llm_outputs(
        where: {
          output: {
            modelPromptTemplate: {
              template: { outputType: { _in: [SOAP_NOTE, DYNAMIC_OUTPUT] } }
            }
          }
        }
        limit: 1
        orderBy: { output: { createdAt: DESC } }
      ) {
        ...AppointmentLlmOutput
      }
    }
  `,
  [appointmentLlmJobFragment, appointmentLlmOutputFragment],
);

export const useAppointmentActivity = () => {
  const id = useAppointmentId();

  const data = useCompleteFragment({
    fragment: appointmentActivityFragment,
    key: {
      id,
    },
  });

  const {
    updatedAt,
    isSummarizationInProgress,
    isTranscriptionInProgress,
    isPendingAudioFileUpload,
    isAnySummarizationInProgress,
  } = data;

  const appointmentLlmJobs = data.appointment_llm_jobs ?? [];
  const latestOutput = data.appointment_llm_outputs?.[0];

  const { isSummarizing, latestSummaryStatus, latestSummaryJobCreatedAt } =
    checkSummarizationStatus(isSummarizationInProgress, appointmentLlmJobs);

  const latestSummaryFailureType =
    latestOutput?.output?.status === 'FAILED'
      ? (latestOutput.output.failureType ?? null)
      : null;

  return {
    updatedAt,
    latestSummaryJobCreatedAt,
    latestSummaryStatus,
    latestSummaryFailureType,
    isSummarizationInProgress: isSummarizing,
    isAnySummarizationInProgress,
    isPendingAudioFileUpload: Boolean(isPendingAudioFileUpload),
    isTranscriptionInProgress:
      Boolean(isTranscriptionInProgress) || Boolean(isPendingAudioFileUpload),
    appointmentLlmJobs,
  };
};
